/** `sections/explore-carousel.liquid` — `<explore-carousel>`: tabbed group of
 *  horizontally scrollable card carousels.
 *
 *  Desktop (>768px): a single tab bar reveals one rail at a time; a single shared
 *  pagination controls the active rail. Boosters are instantiated lazily so hidden
 *  panels can be measured correctly on first activation.
 *
 *  Mobile (≤768px): tabs and the shared pagination are hidden; every panel is shown
 *  at once, each with its own label and its own pagination. Each rail scrolls and
 *  paginates independently of the others.
 */

import ScrollBooster from 'scrollbooster'
import { CarouselPagination } from './carousel-pagination.js'

const MOBILE_QUERY = '(max-width: 768px)'

const TAB_SELECTOR = '[data-explore-carousel-tab]'
const PANEL_SELECTOR = '[data-explore-carousel-panel]'
const CARD_SELECTOR = '[data-explore-carousel-card]'
const RAIL_PREV_SELECTOR = '[data-explore-carousel-rail-prev]'
const RAIL_NEXT_SELECTOR = '[data-explore-carousel-rail-next]'
const RAIL_PAGINATION_SELECTOR = '[data-explore-carousel-rail-pagination]'
const TOGGLE_SELECTOR = '[data-experience-toggle]'
const EXPERIENCE_CARD_SELECTOR = '[data-experience-card]'
const CARD_URL_ATTR = 'data-experience-card-url'
const EXPANDED_OVERLAY_SELECTOR = 'explore-experience-expanded'
const REVIEW_SELECTOR = '[data-experience-review]'
const GRAB_CLASS = 'is-grabbing'
const EXPANDED_CLASS = 'is-expanded'

class ExploreCarousel extends HTMLElement {
  #abort
  #tabButtons = []
  #panels = new Map()
  #viewports = new Map()
  #tracks = new Map()
  #boosters = new Map()
  #rails = new Map()
  #resizeObserver
  #mql
  #activeTab = null
  #reviewReveal = new WeakMap()
  #expandOverlay = null
  #expandPanelKey = null

  connectedCallback() {
    this.#abort = new AbortController()
    const { signal } = this.#abort

    this.#tabButtons = Array.from(this.querySelectorAll(TAB_SELECTOR))

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to CarouselPagination's default when the attribute is missing/invalid.
    const autoplay = Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || true

    this.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
      const key = panel.dataset.exploreCarouselPanel
      if (!key) return
      this.#panels.set(key, panel)
      // Some panels (e.g. the full-width "Try something new" block) have no
      // scrollable track; they still register for tab switching but get no booster.
      const viewport = panel.querySelector(`[data-explore-carousel-viewport="${key}"]`)
      const track = panel.querySelector(`[data-explore-carousel-track="${key}"]`)
      if (!viewport || !track) return
      this.#viewports.set(key, viewport)
      this.#tracks.set(key, track)

      // Each rail owns its overlay arrows + dot pagination. On desktop only the
      // active panel is visible, so its controls double as the section controls.
      const railPrev = panel.querySelector(RAIL_PREV_SELECTOR)
      const railNext = panel.querySelector(RAIL_NEXT_SELECTOR)
      const pagination = new CarouselPagination(panel.querySelector(RAIL_PAGINATION_SELECTOR), {
        signal,
        onSelect: (x) => this.#boosters.get(key)?.scrollTo({ x }),
        autoplay,
        pauseOn: panel,
      })
      this.#rails.set(key, { prev: railPrev, next: railNext, pagination })
      railPrev?.addEventListener('click', () => this.#scrollPanel(key, -1), { signal })
      railNext?.addEventListener('click', () => this.#scrollPanel(key, 1), { signal })
    })

    this.#tabButtons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => this.#activate(btn.dataset.exploreCarouselTab),
        { signal }
      )
    })

    this.addEventListener('click', this.#onToggleClick, { signal })

    // Mobile-only full-screen takeover: the `+` toggle opens this overlay instead
    // of expanding the card inline. On close it reports the last-viewed index so
    // the matching rail can scroll to it.
    this.#expandOverlay = this.querySelector(EXPANDED_OVERLAY_SELECTOR)
    this.addEventListener('experience-expanded:close', this.#onExpandedClose, { signal })

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => this.#refreshMetrics())
      this.#viewports.forEach((vp) => this.#resizeObserver.observe(vp))
      this.#tracks.forEach((t) => this.#resizeObserver.observe(t))
    }

    this.#mql = globalThis.matchMedia?.(MOBILE_QUERY)
    this.#mql?.addEventListener('change', this.#applyMode, { signal })

    this.#activeTab = this.#tabButtons[0]?.dataset.exploreCarouselTab ?? null
    this.#applyMode()
  }

  disconnectedCallback() {
    this.#abort?.abort()
    this.#resizeObserver?.disconnect()
    this.#boosters.forEach((b) => b.destroy())
    this.#boosters.clear()
    this.#rails.forEach((rail) => rail.pagination?.destroy())
  }

  get #isMobile() {
    return Boolean(this.#mql?.matches)
  }

  #applyMode = () => {
    if (this.#isMobile) this.#showAll()
    else if (this.#activeTab) this.#activate(this.#activeTab)
  }

  // Mobile: reveal every panel, build all boosters, and sync each rail independently.
  #showAll() {
    this.#panels.forEach((panel, key) => {
      panel.removeAttribute('hidden')
      this.#ensureBooster(key)
    })
    requestAnimationFrame(() => this.#refreshMetrics())
  }

  // Desktop: only one panel is visible at a time.
  #activate(key) {
    if (!key || !this.#panels.has(key)) return
    this.#activeTab = key

    this.#tabButtons.forEach((btn) => {
      const isActive = btn.dataset.exploreCarouselTab === key
      btn.classList.toggle('is-active', isActive)
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })

    this.#panels.forEach((panel, panelKey) => {
      const isActive = panelKey === key
      panel.classList.toggle('is-active', isActive)
      if (isActive) {
        panel.removeAttribute('hidden')
      } else {
        panel.setAttribute('hidden', '')
      }
    })

    this.#ensureBooster(key)
    this.#boosters.get(key)?.updateMetrics()
    this.#syncRail(key)
  }

  #ensureBooster(key) {
    if (this.#boosters.has(key)) return
    const viewport = this.#viewports.get(key)
    const track = this.#tracks.get(key)
    if (!viewport || !track) return

    const booster = new ScrollBooster({
      viewport,
      content: track,
      scrollMode: 'transform',
      direction: 'horizontal',
      pointerMode: 'touch',
      emulateScroll: true,
      preventDefaultOnEmulateScroll: 'horizontal',
      lockScrollOnDragDirection: 'horizontal',
      bounce: true,
      bounceForce: 0.1,
      textSelection: true,
      onPointerDown: () => viewport.classList.add(GRAB_CLASS),
      onPointerUp: () => viewport.classList.remove(GRAB_CLASS),
      onUpdate: (state) => this.#syncRail(key, state),
    })

    this.#boosters.set(key, booster)
  }

  #refreshMetrics() {
    if (this.#isMobile) {
      this.#boosters.forEach((b) => b.updateMetrics())
      this.#rails.forEach((_, key) => this.#syncRail(key))
    } else {
      this.#boosters.get(this.#activeTab)?.updateMetrics()
      this.#syncRail(this.#activeTab)
    }
  }

  #onToggleClick = (event) => {
    const toggle = event.target.closest(TOGGLE_SELECTOR)
    if (!toggle || !this.contains(toggle)) return
    const card = toggle.closest(EXPERIENCE_CARD_SELECTOR)
    if (!card) return

    const panel = card.closest(PANEL_SELECTOR)
    const key = panel?.dataset.exploreCarouselPanel ?? this.#activeTab

    // Mobile: the `+` acts as the card's CTA — navigate to the card's link if
    // one is set. Falls back to the full-screen takeover when there's no link.
    if (this.#isMobile) {
      const url = card.getAttribute(CARD_URL_ATTR)
      if (url) {
        globalThis.location.href = url
        return
      }
      if (this.#expandOverlay) {
        const track = this.#tracks.get(key)
        const cards = track ? Array.from(track.querySelectorAll(CARD_SELECTOR)) : []
        const index = Math.max(0, cards.indexOf(card))
        this.#expandPanelKey = key
        this.#expandOverlay.open(index)
        return
      }
    }

    // Drop any pending transition listener from a previous toggle on this card.
    const pending = this.#reviewReveal.get(card)
    if (pending) {
      card.removeEventListener('transitionend', pending)
      this.#reviewReveal.delete(card)
    }

    const willExpand = !card.classList.contains(EXPANDED_CLASS)
    toggle.setAttribute('aria-expanded', willExpand ? 'true' : 'false')

    const update = () => {
      this.#boosters.get(key)?.updateMetrics()
      this.#syncRail(key)
    }

    if (willExpand) {
      card.classList.add(EXPANDED_CLASS)
      update()
      // Keep the review hidden until the width animation finishes, then fade it in.
      const onWidthEnd = (e) => {
        if (e.target !== card || e.propertyName !== 'width') return
        card.removeEventListener('transitionend', onWidthEnd)
        this.#reviewReveal.delete(card)
        update()
        card.classList.add('is-review-ready')
        requestAnimationFrame(() => {
          requestAnimationFrame(() => card.classList.add('is-review-visible'))
        })
      }
      this.#reviewReveal.set(card, onWidthEnd)
      card.addEventListener('transitionend', onWidthEnd)
      return
    }

    // Collapse: shrink the width only after the review has faded out.
    const collapseWidth = () => {
      card.classList.remove('is-review-ready', EXPANDED_CLASS)
      update()
      const onWidthEnd = (e) => {
        if (e.target !== card || e.propertyName !== 'width') return
        card.removeEventListener('transitionend', onWidthEnd)
        this.#reviewReveal.delete(card)
        update()
      }
      this.#reviewReveal.set(card, onWidthEnd)
      card.addEventListener('transitionend', onWidthEnd)
    }

    const review = card.querySelector(REVIEW_SELECTOR)
    const wasVisible = card.classList.contains('is-review-visible')
    card.classList.remove('is-review-visible')

    if (wasVisible && review) {
      const onFadeOut = (e) => {
        if (e.target !== review || e.propertyName !== 'opacity') return
        card.removeEventListener('transitionend', onFadeOut)
        this.#reviewReveal.delete(card)
        collapseWidth()
      }
      this.#reviewReveal.set(card, onFadeOut)
      card.addEventListener('transitionend', onFadeOut)
    } else {
      // Review was never shown; collapse straight away.
      collapseWidth()
    }
  }

  #onExpandedClose = (event) => {
    const key = this.#expandPanelKey
    if (!key) return
    const index = event.detail?.index ?? 0
    this.#scrollPanelToCard(key, index)
  }

  #scrollPanelToCard(key, index) {
    const booster = this.#boosters.get(key)
    if (!booster) return
    const step = this.#cardStep(key)
    const max = this.#maxScroll(key)
    const target = Math.max(0, Math.min(max, index * step))
    booster.scrollTo({ x: target })
  }

  #scrollPanel(key, direction) {
    const booster = this.#boosters.get(key)
    if (!booster) return
    const step = this.#cardStep(key)
    const current = booster.getState().position.x
    const max = this.#maxScroll(key)
    const target = Math.max(0, Math.min(max, current + direction * step))
    booster.scrollTo({ x: target })
  }

  #cardStep(key) {
    const track = this.#tracks.get(key)
    const viewport = this.#viewports.get(key)
    if (!track || !viewport) return 0
    const cards = track.querySelectorAll(CARD_SELECTOR)
    if (cards.length === 0) return viewport.clientWidth
    const first = cards[0]
    const second = cards[1]
    if (second) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left
    }
    const style = globalThis.getComputedStyle(track)
    const gap = Number.parseFloat(style.columnGap || style.gap || '0') || 0
    return first.getBoundingClientRect().width + gap
  }

  #maxScroll(key) {
    const track = this.#tracks.get(key)
    const viewport = this.#viewports.get(key)
    if (!track || !viewport) return 0
    return Math.max(0, track.scrollWidth - viewport.clientWidth)
  }

  #syncRail(key, state) {
    const rail = this.#rails.get(key)
    if (!rail) return
    const booster = this.#boosters.get(key)
    const max = this.#maxScroll(key)
    const hasOverflow = max > 1
    const current = booster ? (state ?? booster.getState()).position.x : 0
    const atStart = current <= 1
    const atEnd = current >= max - 1

    if (rail.prev) rail.prev.disabled = !hasOverflow || atStart
    if (rail.next) rail.next.disabled = !hasOverflow || atEnd
    rail.pagination?.update({ current, max, step: this.#cardStep(key) })
  }
}

if (!customElements.get('explore-carousel')) {
  customElements.define('explore-carousel', ExploreCarousel)
}
